import React, { useState, useEffect, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithPopup, GoogleAuthProvider, signOut, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, where, onSnapshot, Timestamp, setDoc } from 'firebase/firestore';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ChevronRight, Plus, Trash2, Edit, Save, X, BarChart2, Settings, Home, AlertTriangle, Download, LogIn, LogOut, User } from 'lucide-react';

// Firebase configuration
let firebaseConfig;
let appId;

try {
    const configString = process.env.REACT_APP_FIREBASE_CONFIG;
    appId = process.env.REACT_APP_APP_ID;

    console.log('Environment variables check:');
    console.log('REACT_APP_FIREBASE_CONFIG exists:', !!configString);
    console.log('REACT_APP_APP_ID:', appId);
    console.log('REACT_APP_ALLOWED_USERS:', process.env.REACT_APP_ALLOWED_USERS);

    if (!configString) {
        throw new Error('REACT_APP_FIREBASE_CONFIG environment variable is not set');
    }

    if (!appId) {
        throw new Error('REACT_APP_APP_ID environment variable is not set');
    }

    firebaseConfig = JSON.parse(configString);
    console.log('Firebase config loaded successfully');
    console.log('Project ID:', firebaseConfig.projectId);

} catch (error) {
    console.error('Firebase configuration error:', error);
    alert(`Firebase設定エラー: ${error.message}\n\n.envファイルを確認してください。`);
    firebaseConfig = {
        apiKey: "demo",
        authDomain: "demo.firebaseapp.com",
        projectId: "demo",
        storageBucket: "demo.appspot.com",
        messagingSenderId: "123456789",
        appId: "demo"
    };
    appId = "demo";
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// 認証されたユーザーのUID（アクセス許可リスト）
const ALLOWED_USERS = process.env.REACT_APP_ALLOWED_USERS ? process.env.REACT_APP_ALLOWED_USERS.split(',') : [];

// Google認証プロバイダー
const googleProvider = new GoogleAuthProvider();

// --- Helper Functions ---
const formatDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : date.toDate();
    return d.toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const getLocalDateString = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : date.toDate();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatShortDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : date.toDate();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${month}/${day}`;
};


// --- Main App Component ---
export default function App() {
    const [page, setPage] = useState('home'); // 'home', 'graph', 'admin', 'labelDetail'
    const [labels, setLabels] = useState([]);
    const [events, setEvents] = useState([]);
    const [moods, setMoods] = useState({});
    const [loading, setLoading] = useState(true);
    const [currentLabel, setCurrentLabel] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [userId, setUserId] = useState(null);
    const [user, setUser] = useState(null);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState({ show: false, type: null, id: null, message: '' });

    // --- Firebase Authentication ---
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setUser(user);
                setUserId(user.uid);
                
                // ユーザーが許可リストに含まれているかチェック
                const isUserAllowed = ALLOWED_USERS.length === 0 || ALLOWED_USERS.includes(user.uid) || ALLOWED_USERS.includes(user.email);
                setIsAuthorized(isUserAllowed);
                setIsAuthReady(true);
            } else {
                setUser(null);
                setUserId(null);
                setIsAuthorized(false);
                setIsAuthReady(true);
            }
        });

        return () => unsubscribe();
    }, []);

    // --- 認証関連のハンドラー ---
    const handleGoogleSignIn = async () => {
        try {
            console.log('Google サインインを開始します...');
            const result = await signInWithPopup(auth, googleProvider);
            console.log('サインイン成功:', result.user);
        } catch (error) {
            console.error("Google sign-in error:", error);
            console.error("Error code:", error.code);
            console.error("Error message:", error.message);
            
            let errorMessage = 'サインインに失敗しました。';
            switch (error.code) {
                case 'auth/unauthorized-domain':
                    errorMessage = 'このドメインは認証に許可されていません。Firebase Consoleでドメインを追加してください。';
                    break;
                case 'auth/popup-blocked':
                    errorMessage = 'ポップアップがブロックされました。ブラウザの設定を確認してください。';
                    break;
                case 'auth/popup-closed-by-user':
                    errorMessage = 'サインインがキャンセルされました。';
                    break;
                case 'auth/network-request-failed':
                    errorMessage = 'ネットワークエラーが発生しました。インターネット接続を確認してください。';
                    break;
                default:
                    errorMessage = `サインインエラー: ${error.message}`;
            }
            
            alert(errorMessage);
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut(auth);
        } catch (error) {
            console.error("Sign-out error:", error);
        }
    };


    // --- Firestore Data Fetching ---
    useEffect(() => {
        if (!isAuthReady || !userId || !isAuthorized) return;

        setLoading(true);
        
        const labelsCollectionPath = `artifacts/${appId}/public/data/labels`;
        const eventsCollectionPath = `artifacts/${appId}/public/data/events`;
        const moodsCollectionPath = `artifacts/${appId}/public/data/moods`;

        const unsubLabels = onSnapshot(query(collection(db, labelsCollectionPath)), (snapshot) => {
            const labelsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            labelsData.sort((a, b) => {
                const aTime = a.createdAt?.toMillis() || 0;
                const bTime = b.createdAt?.toMillis() || 0;
                return aTime - bTime;
            });

            setLabels(labelsData);
        }, (error) => console.error("Error fetching labels:", error));

        const unsubEvents = onSnapshot(query(collection(db, eventsCollectionPath)), (snapshot) => {
            const eventsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setEvents(eventsData);
        }, (error) => console.error("Error fetching events:", error));
        
        const unsubMoods = onSnapshot(query(collection(db, moodsCollectionPath)), (snapshot) => {
            const moodsData = {};
            snapshot.forEach(doc => {
                moodsData[doc.id] = doc.data().mood;
            });
            setMoods(moodsData);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching moods:", error);
            setLoading(false);
        });


        return () => {
            unsubLabels();
            unsubEvents();
            unsubMoods();
        };
    }, [isAuthReady, userId, isAuthorized]);

    // --- Firestore Handlers ---
    const handleAddOrUpdateMood = async (mood) => {
        const dateString = getLocalDateString(new Date());
        const moodsCollectionPath = `artifacts/${appId}/public/data/moods`;
        try {
            if (mood) {
                await setDoc(doc(db, moodsCollectionPath, dateString), {
                    mood: mood,
                    timestamp: Timestamp.now()
                });
            } else {
                await deleteDoc(doc(db, moodsCollectionPath, dateString));
            }
        } catch (error) {
            console.error("Error saving mood: ", error);
        }
    };

    const handleAddLabel = async (name, color) => {
        if (!name.trim()) return;
        try {
            const labelsCollectionPath = `artifacts/${appId}/public/data/labels`;
            await addDoc(collection(db, labelsCollectionPath), { name, color, createdAt: Timestamp.now() });
        } catch (error) {
            console.error("Error adding label: ", error);
        }
    };

    const handleUpdateLabel = async (id, newName) => {
        if (!newName.trim()) return;
        try {
            const labelDocPath = `artifacts/${appId}/public/data/labels/${id}`;
            await updateDoc(doc(db, labelDocPath), { name: newName });
        } catch (error) {
            console.error("Error updating label: ", error);
        }
    };

    const handleDeleteLabel = async (id) => {
        setShowDeleteConfirm({ show: false, type: null, id: null, message: '' });
        try {
            const eventsCollectionPath = `artifacts/${appId}/public/data/events`;
            const q = query(collection(db, eventsCollectionPath), where("labelId", "==", id));
            const querySnapshot = await getDocs(q);
            
            const deletePromises = querySnapshot.docs.map(eventDoc => 
                deleteDoc(doc(db, eventsCollectionPath, eventDoc.id))
            );
            await Promise.all(deletePromises);

            const labelDocPath = `artifacts/${appId}/public/data/labels/${id}`;
            await deleteDoc(doc(db, labelDocPath));
        } catch (error) {
            console.error("Error deleting label and associated events: ", error);
        }
    };

    const handleAddEvent = async (labelId) => {
        try {
            const eventsCollectionPath = `artifacts/${appId}/public/data/events`;
            await addDoc(collection(db, eventsCollectionPath), { labelId, timestamp: Timestamp.now() });
        } catch (error) {
            console.error("Error adding event: ", error);
        }
    };
    
    const handleAddManualEvent = async (labelId, dateTimeString) => {
        if (!dateTimeString) return;
        const date = new Date(dateTimeString);
        if (isNaN(date.getTime())) {
            console.error("Invalid date format.");
            return;
        }
        try {
            const eventsCollectionPath = `artifacts/${appId}/public/data/events`;
            await addDoc(collection(db, eventsCollectionPath), { labelId, timestamp: Timestamp.fromDate(date) });
        } catch (error) {
            console.error("Error adding manual event:", error);
        }
    };

    const handleUpdateEvent = async (eventId, newDateTimeString) => {
        if (!newDateTimeString) return;
        const date = new Date(newDateTimeString);
        if (isNaN(date.getTime())) {
            console.error("Invalid date format.");
            return;
        }
        try {
            const eventDocPath = `artifacts/${appId}/public/data/events/${eventId}`;
            await updateDoc(doc(db, eventDocPath), { timestamp: Timestamp.fromDate(date) });
        } catch (error) {
            console.error("Error updating event: ", error);
        }
    };

    const handleDeleteEvent = async (eventId) => {
        setShowDeleteConfirm({ show: false, type: null, id: null, message: '' });
        try {
            const eventDocPath = `artifacts/${appId}/public/data/events/${eventId}`;
            await deleteDoc(doc(db, eventDocPath));
        } catch (error) {
            console.error("Error deleting event:", error);
        }
    };

    const confirmDelete = (type, id) => {
        const message = type === 'label' 
            ? 'このラベルと関連する全ての記録を削除します。よろしいですか？'
            : 'この記録を削除しますか？';
        setShowDeleteConfirm({ show: true, type, id, message });
    };

    const processDelete = () => {
        if (showDeleteConfirm.type === 'label') {
            handleDeleteLabel(showDeleteConfirm.id);
        } else if (showDeleteConfirm.type === 'event') {
            handleDeleteEvent(showDeleteConfirm.id);
        }
        setShowDeleteConfirm({ show: false, type: null, id: null, message: '' });
    };

    const navigateToLabelDetail = (label) => {
        setCurrentLabel(label);
        setPage('labelDetail');
    };

    const renderPage = () => {
        if (loading) {
            return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div></div>;
        }
        switch (page) {
            case 'graph': return <GraphScreen labels={labels} events={events} moods={moods} />;
            case 'admin': return <AdminScreen labels={labels} onAddLabel={handleAddLabel} onUpdateLabel={handleUpdateLabel} onDeleteLabel={(id) => confirmDelete('label', id)} onSelectLabel={navigateToLabelDetail} />;
            case 'labelDetail': return <LabelDetailScreen label={currentLabel} events={events.filter(e => e.labelId === currentLabel.id)} onAddEvent={handleAddManualEvent} onUpdateEvent={handleUpdateEvent} onDeleteEvent={(id) => confirmDelete('event', id)} />;
            default: return <HomeScreen labels={labels} onAddEvent={handleAddEvent} moods={moods} onSaveMood={handleAddOrUpdateMood} />;
        }
    };

    return (
        <div className="bg-gray-100 min-h-screen font-sans text-gray-800">
            {!isAuthReady ? (
                // 認証状態を確認中
                <div className="flex justify-center items-center h-screen">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        <p className="text-gray-600">認証状態を確認中...</p>
                    </div>
                </div>
            ) : !user ? (
                // 未ログイン状態
                <div className="flex justify-center items-center h-screen">
                    <div className="bg-white rounded-lg shadow-xl p-8 m-4 max-w-md w-full text-center">
                        <div className="mb-6">
                            <User className="mx-auto mb-4 text-gray-400" size={64} />
                            <h1 className="text-2xl font-bold text-gray-700 mb-2">体調管理アプリ</h1>
                            <p className="text-gray-600">アクセスするにはGoogleアカウントでサインインしてください。</p>
                        </div>
                        <button 
                            onClick={handleGoogleSignIn}
                            className="w-full bg-blue-500 text-white px-6 py-3 rounded-md shadow hover:bg-blue-600 flex items-center justify-center gap-2 transition-colors font-medium"
                        >
                            <LogIn size={20} />
                            Googleでサインイン
                        </button>
                    </div>
                </div>
            ) : !isAuthorized ? (
                // 認証済みだがアクセス権限なし
                <div className="flex justify-center items-center h-screen">
                    <div className="bg-white rounded-lg shadow-xl p-8 m-4 max-w-md w-full text-center">
                        <div className="mb-6">
                            <AlertTriangle className="mx-auto mb-4 text-red-400" size={64} />
                            <h1 className="text-2xl font-bold text-gray-700 mb-2">アクセス権限なし</h1>
                            <p className="text-gray-600 mb-4">
                                申し訳ございませんが、このアプリケーションにアクセスする権限がありません。
                            </p>
                            <p className="text-sm text-gray-500 mb-4">
                                ログインアカウント: {user.email}
                            </p>
                        </div>
                        <button 
                            onClick={handleSignOut}
                            className="w-full bg-gray-500 text-white px-6 py-3 rounded-md shadow hover:bg-gray-600 flex items-center justify-center gap-2 transition-colors font-medium"
                        >
                            <LogOut size={20} />
                            サインアウト
                        </button>
                    </div>
                </div>
            ) : (
                // 認証済みでアクセス権限あり
                <>
                    {showDeleteConfirm.show && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                            <div className="bg-white rounded-lg shadow-xl p-6 m-4 max-w-sm w-full">
                                <div className="flex items-center mb-4">
                                    <AlertTriangle className="text-red-500 mr-3" size={24}/>
                                    <h3 className="text-lg font-bold">削除の確認</h3>
                                </div>
                                <p className="text-gray-600 mb-6">{showDeleteConfirm.message}</p>
                                <div className="flex justify-end space-x-4">
                                    <button onClick={() => setShowDeleteConfirm({ show: false, type: null, id: null, message: '' })} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">キャンセル</button>
                                    <button onClick={processDelete} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">削除</button>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="container mx-auto p-4 max-w-4xl">
                        <header className="bg-white rounded-lg shadow-md p-4 mb-6 flex justify-between items-center">
                            <h1 className="text-2xl font-bold text-gray-700">体調管理アプリ</h1>
                            <div className="flex items-center gap-4">
                                <div className="hidden sm:block text-right">
                                    <p className="text-sm text-gray-600">{user.displayName || user.email}</p>
                                    <p className="text-xs text-gray-400">App ID: {appId}</p>
                                </div>
                                <button 
                                    onClick={handleSignOut}
                                    className="p-2 text-gray-500 hover:bg-gray-200 rounded-full transition-colors"
                                    title="サインアウト"
                                >
                                    <LogOut size={20} />
                                </button>
                            </div>
                        </header>
                        <main className="bg-white rounded-lg shadow-md p-6">
                            {renderPage()}
                        </main>
                        <footer className="mt-6">
                            <nav className="flex justify-center space-x-2 sm:space-x-4 bg-white p-3 rounded-full shadow-md">
                                <button onClick={() => setPage('graph')} className={`flex flex-col sm:flex-row items-center space-x-2 p-3 rounded-full transition-colors duration-200 ${page === 'graph' ? 'bg-blue-500 text-white' : 'hover:bg-gray-200'}`}><BarChart2 size={20} /> <span className="text-xs sm:text-sm">グラフ</span></button>
                                <button onClick={() => setPage('home')} className={`flex flex-col sm:flex-row items-center space-x-2 p-3 rounded-full transition-colors duration-200 ${page === 'home' ? 'bg-blue-500 text-white' : 'hover:bg-gray-200'}`}><Home size={20} /> <span className="text-xs sm:text-sm">ホーム</span></button>
                                <button onClick={() => setPage('admin')} className={`flex flex-col sm:flex-row items-center space-x-2 p-3 rounded-full transition-colors duration-200 ${page === 'admin' || page === 'labelDetail' ? 'bg-blue-500 text-white' : 'hover:bg-gray-200'}`}><Settings size={20} /> <span className="text-xs sm:text-sm">管理</span></button>
                            </nav>
                        </footer>
                    </div>
                </>
            )}
        </div>
    );
}

// --- Page Components ---

function HomeScreen({ labels, onAddEvent, moods, onSaveMood }) {
    const [showConfirm, setShowConfirm] = useState(null);
    const todayString = useMemo(() => getLocalDateString(new Date()), []);
    const [selectedMood, setSelectedMood] = useState('');

    useEffect(() => {
        setSelectedMood(moods[todayString] || '');
    }, [moods, todayString]);

    const handleMoodChange = (e) => {
        const newMood = e.target.value;
        setSelectedMood(newMood);
        onSaveMood(newMood);
    };

    const handleButtonClick = (label) => {
        setShowConfirm(label);
        onAddEvent(label.id);
        setTimeout(() => setShowConfirm(null), 1500);
    };

    return (
        <div>
            <div className="mb-8 p-4 bg-blue-50 rounded-lg">
                <label htmlFor="mood-select" className="block text-lg font-semibold text-gray-700 mb-2">今日の気分は？</label>
                <select 
                    id="mood-select" 
                    value={selectedMood} 
                    onChange={handleMoodChange} 
                    className="w-full p-3 text-lg border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                >
                    <option value="">選択してください...</option>
                    <option value="☺️">☺️ 良い</option>
                    <option value="😐">😐 普通</option>
                    <option value="😨">😨 悪い</option>
                </select>
            </div>

            <h2 className="text-xl font-semibold mb-4">イベントを記録</h2>
            <p className="text-gray-600 mb-6">ボタンをタップして、今の状態を記録しましょう。</p>
            
            {labels.length === 0 ? (
                 <div className="text-center py-10"><p className="text-gray-500">まだラベルが登録されていません。「管理」画面から追加してください。</p></div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {labels.map(label => (
                        <button key={label.id} onClick={() => handleButtonClick(label)} className="relative p-4 rounded-lg text-white font-bold shadow-lg transform hover:scale-105 transition-transform duration-200 h-24 flex items-center justify-center text-center" style={{ backgroundColor: label.color }}>
                            {showConfirm && showConfirm.id === label.id ? <span className="animate-pulse">記録！</span> : <span>{label.name}</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function GraphScreen({ labels, events, moods }) {
    const [selectedLabels, setSelectedLabels] = useState([]);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    
    useEffect(() => {
        setSelectedLabels(labels.map(l => l.id));
    }, [labels]);

    const handleLabelToggle = (labelId) => {
        setSelectedLabels(prev =>
            prev.includes(labelId) ? prev.filter(id => id !== labelId) : [...prev, labelId]
        );
    };

    // CSV ダウンロード機能
    const generateCSV = (startDate, endDate) => {
        // 気分の絵文字を文字に変換
        const moodToText = {
            '☺️': '良い',
            '😐': '普通', 
            '😨': '悪い'
        };

        // 期間内の全ての日付を生成
        const dates = [];
        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
            dates.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }

        // ヘッダー行を作成（日付、各ラベル名、気分）
        const headers = ['日付', ...labels.map(l => l.name), '気分'];

        // 各日付のデータを作成
        const rows = dates.map(date => {
            const dateString = getLocalDateString(date);
            const row = [dateString];

            // 各ラベルについて、その日のイベント時間を記録
            labels.forEach(label => {
                const dayEvents = events.filter(event => {
                    const eventDate = event.timestamp.toDate();
                    return event.labelId === label.id && 
                           getLocalDateString(eventDate) === dateString;
                });

                if (dayEvents.length > 0) {
                    // 複数のイベントがある場合は時間をカンマ区切りで表示
                    const times = dayEvents.map(event => {
                        const eventDate = event.timestamp.toDate();
                        return eventDate.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
                    }).join(', ');
                    row.push(times);
                } else {
                    row.push('');
                }
            });

            // 気分を追加（絵文字を文字に変換）
            const mood = moods[dateString] || '';
            const moodText = moodToText[mood] || mood;
            row.push(moodText);

            return row;
        });

        // CSV形式に変換
        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${cell}"`).join(','))
            .join('\n');

        return csvContent;
    };

    const downloadCSV = (period) => {
        const now = new Date();
        let startDate, endDate;
        let filename;

        switch (period) {
            case '1month':
                startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                endDate = now;
                filename = 'health-data-1month.csv';
                break;
            case '3months':
                startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
                endDate = now;
                filename = 'health-data-3months.csv';
                break;
            case '1year':
                startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
                endDate = now;
                filename = 'health-data-1year.csv';
                break;
            case 'custom':
                if (!customStartDate || !customEndDate) {
                    alert('開始日と終了日を指定してください');
                    return;
                }
                startDate = new Date(customStartDate);
                endDate = new Date(customEndDate);
                filename = `health-data-${customStartDate}-${customEndDate}.csv`;
                break;
            default:
                return;
        }

        const csvContent = generateCSV(startDate, endDate);
        
        // BOMを追加してExcelで文字化けを防ぐ
        const bom = '\uFEFF';
        const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
        
        // ダウンロード
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };
    
    const { scatterData, xDomain, yDomain, yAxisTicks, xTicks } = useMemo(() => {
        if (labels.length === 0) {
            const now = new Date();
            const xDomain = [now.getTime() - 30 * 24 * 60 * 60 * 1000, now.getTime()];
            return { scatterData: [], xDomain, yDomain: [-0.5, 0.5], yAxisTicks: [], xTicks: [] };
        }

        // --- X-Axis Setup (Numerical Timestamp) ---
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setHours(0, 0, 0, 0);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29);
        const xDomain = [thirtyDaysAgo.getTime(), today.getTime()];
        const xTicks = [];
        for (let i = 0; i < 30; i += 1) { // 1日ごとに目盛りを生成
            const d = new Date(thirtyDaysAgo);
            d.setDate(d.getDate() + i);
            xTicks.push(d.getTime());
        }

        // --- Y-Axis Setup (Numerical Index) ---
        const yAxisTicks = labels.map(l => l.name);
        const yDomain = [-0.5, labels.length - 0.5];
        const yDomainMap = new Map(labels.map((l, index) => [l.id, index]));

        // --- Data Processing ---
        const recentEvents = events.filter(event => {
            const eventDate = event.timestamp.toDate();
            return eventDate >= thirtyDaysAgo;
        });

        const dataByLabel = {};
        labels.forEach(l => {
            dataByLabel[l.id] = { id: l.id, name: l.name, color: l.color, data: [] };
        });

        recentEvents.forEach(event => {
            const label = labels.find(l => l.id === event.labelId);
            if (label && dataByLabel[label.id]) {
                const yValue = yDomainMap.get(label.id);
                if (yValue !== undefined) {
                    dataByLabel[label.id].data.push({
                        x: event.timestamp.toDate().getTime(),
                        y: yValue,
                    });
                }
            }
        });

        return { 
            scatterData: Object.values(dataByLabel), 
            xDomain, 
            yDomain, 
            yAxisTicks,
            xTicks,
        };
    }, [events, labels]);

    const filteredScatterData = useMemo(() => {
        return scatterData.filter(series => selectedLabels.includes(series.id));
    }, [scatterData, selectedLabels]);

    const CustomXAxisTickWithMood = (props) => {
        const { x, y, payload } = props;
        const { value } = payload;
    
        const date = new Date(value);
        const dateString = getLocalDateString(date);
        const mood = moods[dateString] || '';
        const formattedDate = formatShortDate(date);

        const displayText = mood ? `${mood} ${formattedDate}` : formattedDate;
    
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={12} y={10} dy={16} textAnchor="end" fill="#666" transform="rotate(-60)">
                    {displayText}
                </text>
            </g>
        );
    };

    return (
        <div>
            <h2 className="text-xl font-semibold mb-4">イベント発生マップ (過去30日間)</h2>
            
            {/* CSVダウンロードセクション */}
            <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Download size={20} />
                    データをCSVでダウンロード
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                    <button
                        onClick={() => downloadCSV('1month')}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm"
                    >
                        過去1ヶ月
                    </button>
                    <button
                        onClick={() => downloadCSV('3months')}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm"
                    >
                        過去3ヶ月
                    </button>
                    <button
                        onClick={() => downloadCSV('1year')}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm"
                    >
                        過去1年
                    </button>
                    <button
                        onClick={() => setShowDatePicker(!showDatePicker)}
                        className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors text-sm"
                    >
                        期間指定
                    </button>
                </div>
                
                {showDatePicker && (
                    <div className="p-4 bg-white rounded-md border border-gray-200">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">開始日</label>
                                <input
                                    type="date"
                                    value={customStartDate}
                                    onChange={(e) => setCustomStartDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">終了日</label>
                                <input
                                    type="date"
                                    value={customEndDate}
                                    onChange={(e) => setCustomEndDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div>
                                <button
                                    onClick={() => downloadCSV('custom')}
                                    className="w-full px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                                >
                                    ダウンロード
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold mb-2">ラベルフィルター</h3>
                <div className="flex flex-wrap gap-2">
                    {labels.map(label => (
                        <button
                            key={label.id}
                            onClick={() => handleLabelToggle(label.id)}
                            className={`px-3 py-1 rounded-full text-sm transition-all duration-200 border-2`}
                            style={{ 
                                backgroundColor: selectedLabels.includes(label.id) ? label.color : '#ffffff',
                                borderColor: label.color,
                                color: selectedLabels.includes(label.id) ? '#ffffff' : label.color
                            }}
                        >
                            {label.name}
                        </button>
                    ))}
                </div>
            </div>
            <div className="w-full overflow-x-auto">
                <div style={{ minWidth: 800, height: 450 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <ScatterChart margin={{ top: 20, right: 20, bottom: 80, left: 30 }}>
                            <CartesianGrid />
                            <XAxis
                                type="number"
                                dataKey="x"
                                name="日付"
                                domain={xDomain}
                                ticks={xTicks}
                                tick={<CustomXAxisTickWithMood />}
                                height={100}
                                interval={0}
                            />
                            <YAxis
                                type="number"
                                dataKey="y"
                                name="ラベル"
                                domain={yDomain}
                                ticks={labels.map((l, index) => index)}
                                tickFormatter={(tick) => yAxisTicks[tick]}
                                width={80}
                                reversed={true}
                            />
                            <Tooltip 
                                cursor={{ strokeDasharray: '3 3' }} 
                                formatter={(value, name) => {
                                    if (name === 'ラベル' && yAxisTicks[value]) {
                                        return yAxisTicks[value];
                                    }
                                    if (name === '日付') return '';
                                    
                                    return value;
                                }}
                                labelFormatter={(unixTime) => formatDate(new Date(unixTime))}
                            />
                            <Legend verticalAlign="top" height={36}/>
                            {filteredScatterData.map(series => (
                                <Scatter key={series.id} name={series.name} data={series.data} fill={series.color} shape="circle" />
                            ))}
                        </ScatterChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}

function AdminScreen({ labels, onAddLabel, onUpdateLabel, onDeleteLabel, onSelectLabel }) {
    const [newLabelName, setNewLabelName] = useState('');
    const [newLabelColor, setNewLabelColor] = useState('#4a90e2');
    const [editingLabelId, setEditingLabelId] = useState(null);
    const [editingLabelName, setEditingLabelName] = useState('');
    const defaultColors = ['#4a90e2', '#50e3c2', '#f5a623', '#f8e71c', '#d0021b', '#bd10e0', '#9013fe', '#4a4a4a', '#7ed321', '#b8e986'];

    const handleAddClick = () => {
        onAddLabel(newLabelName, newLabelColor);
        setNewLabelName('');
        setNewLabelColor('#4a90e2');
    };
    const startEditing = (label) => { setEditingLabelId(label.id); setEditingLabelName(label.name); };
    const cancelEditing = () => { setEditingLabelId(null); setEditingLabelName(''); };
    const saveEditing = (id) => { onUpdateLabel(id, editingLabelName); cancelEditing(); };

    return (
        <div>
            <h2 className="text-xl font-semibold mb-6">ラベル管理</h2>
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h3 className="font-semibold mb-3">新しいラベルを追加</h3>
                <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                    <div className="flex-grow"><label className="block text-sm font-medium text-gray-700 mb-1">ラベル名</label><input type="text" value={newLabelName} onChange={(e) => setNewLabelName(e.target.value)} placeholder="例: 頭痛" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"/></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">色</label><div className="flex items-center gap-2"><input type="color" value={newLabelColor} onChange={(e) => setNewLabelColor(e.target.value)} className="w-10 h-10 p-1 border border-gray-300 rounded-md cursor-pointer"/><div className="flex flex-wrap gap-1 max-w-[150px]">{defaultColors.map(color => (<button key={color} onClick={() => setNewLabelColor(color)} className="w-6 h-6 rounded-full" style={{backgroundColor: color}}></button>))}</div></div></div>
                    <button onClick={handleAddClick} className="bg-blue-500 text-white px-4 py-2 rounded-md shadow hover:bg-blue-600 flex items-center justify-center gap-2 transition-colors"><Plus size={18} /> 追加</button>
                </div>
            </div>
            <div>
                <h3 className="font-semibold mb-3">登録済みラベル一覧</h3>
                <ul className="space-y-3">
                    {labels.map(label => (
                        <li key={label.id} className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 flex items-center justify-between">
                            {editingLabelId === label.id ? (
                                <div className="flex-grow flex items-center gap-2"><input type="text" value={editingLabelName} onChange={(e) => setEditingLabelName(e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded-md"/><button onClick={() => saveEditing(label.id)} className="p-2 text-green-600 hover:bg-green-100 rounded-full"><Save size={18} /></button><button onClick={cancelEditing} className="p-2 text-gray-500 hover:bg-gray-200 rounded-full"><X size={18} /></button></div>
                            ) : (
                                <div className="flex items-center gap-3 flex-grow cursor-pointer" onClick={() => onSelectLabel(label)}><span className="w-4 h-4 rounded-full" style={{ backgroundColor: label.color }}></span><span className="font-medium">{label.name}</span></div>
                            )}
                            <div className="flex items-center gap-2"><button onClick={() => startEditing(label)} className="p-2 text-blue-600 hover:bg-blue-100 rounded-full"><Edit size={18} /></button><button onClick={() => onDeleteLabel(label.id)} className="p-2 text-red-600 hover:bg-red-100 rounded-full"><Trash2 size={18} /></button><button onClick={() => onSelectLabel(label)} className="p-2 text-gray-500 hover:bg-gray-200 rounded-full"><ChevronRight size={18} /></button></div>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

function LabelDetailScreen({ label, events, onAddEvent, onUpdateEvent, onDeleteEvent }) {
    const [newEventDateTime, setNewEventDateTime] = useState('');
    const [editingEvent, setEditingEvent] = useState({ id: null, dateTime: '' });
    const sortedEvents = useMemo(() => [...events].sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate()), [events]);

    const handleAddClick = () => { onAddEvent(label.id, newEventDateTime); setNewEventDateTime(''); };
    const startEditing = (event) => {
        const date = event.timestamp.toDate();
        const localDateTime = new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
        setEditingEvent({ id: event.id, dateTime: localDateTime });
    };
    const cancelEditing = () => setEditingEvent({ id: null, dateTime: '' });
    const saveEditing = () => { onUpdateEvent(editingEvent.id, editingEvent.dateTime); cancelEditing(); };

    return (
        <div>
            <div className="flex items-center gap-3 mb-6"><span className="w-6 h-6 rounded-full" style={{backgroundColor: label.color}}></span><h2 className="text-xl font-semibold">「{label.name}」の記録管理</h2></div>
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h3 className="font-semibold mb-3">記録を手動で追加</h3>
                 <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                    <div className="flex-grow"><label className="block text-sm font-medium text-gray-700 mb-1">日時</label><input type="datetime-local" value={newEventDateTime} onChange={(e) => setNewEventDateTime(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"/></div>
                    <button onClick={handleAddClick} className="bg-blue-500 text-white px-4 py-2 rounded-md shadow hover:bg-blue-600 flex items-center justify-center gap-2 transition-colors"><Plus size={18} /> 追加</button>
                </div>
            </div>
            <div>
                 <h3 className="font-semibold mb-3">記録一覧</h3>
                 <ul className="space-y-2">
                    {sortedEvents.map(event => (
                        <li key={event.id} className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 flex items-center justify-between">
                            {editingEvent.id === event.id ? (
                                <div className="flex-grow flex items-center gap-2"><input type="datetime-local" value={editingEvent.dateTime} onChange={(e) => setEditingEvent({...editingEvent, dateTime: e.target.value})} className="flex-grow px-2 py-1 border border-gray-300 rounded-md"/><button onClick={saveEditing} className="p-2 text-green-600 hover:bg-green-100 rounded-full"><Save size={18} /></button><button onClick={cancelEditing} className="p-2 text-gray-500 hover:bg-gray-200 rounded-full"><X size={18} /></button></div>
                            ) : (
                                <span className="font-mono">{formatDate(event.timestamp)}</span>
                            )}
                            <div className="flex items-center gap-2"><button onClick={() => startEditing(event)} className="p-2 text-blue-600 hover:bg-blue-100 rounded-full"><Edit size={18} /></button><button onClick={() => onDeleteEvent(event.id)} className="p-2 text-red-600 hover:bg-red-100 rounded-full"><Trash2 size={18} /></button></div>
                        </li>
                    ))}
                 </ul>
            </div>
        </div>
    );
}
